
import { defineComponent, reactive, computed } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { UserInfoDetailService } from '../user-info/_components/user-info-detail.service'
import { ToastService } from '../../services/toast.service'
import { UserService } from '@/services/user.service'
import { CookieService } from '@/services/cookie.service'
import AccountManageHeader from './components/AccountManageHeader.vue'
import WechatVerification from './dialog/WechatVerification.vue'
import PhoneVerification from './dialog/PhoneVerification.vue'
import WechatLogin from './dialog/WechatLogin.vue'
import VerificationFail from './dialog/VerificationFail.vue'
import AccountDeleteNotice from './dialog/AccountDeleteNotice.vue'
import { LOGIN_TYPE_ID, ModalType, VERIFICATION_STATUS } from './constants'
import { AccountManageLoginService } from './components/loginApi.service'

// 登录方式配置
const LOGIN_TYPE_CONFIG = {
  [LOGIN_TYPE_ID.PHONE]: {
    id: LOGIN_TYPE_ID.PHONE,
    key: 'phone',
    icon: require('@/views/accountManage/_images/phone-icon.png'),
    name: '手机号'
  },
  [LOGIN_TYPE_ID.WECHAT]: {
    id: LOGIN_TYPE_ID.WECHAT,
    key: 'wechat',
    icon: require('@/views/accountManage/_images/wechat-icon.png'),
    name: '微信账号'
  },
}

/** 用户详情 */
export default defineComponent({
  name: 'AccountManage',
  components: {
    AccountManageHeader,
    WechatVerification,
    PhoneVerification,
    WechatLogin,
    VerificationFail,
    AccountDeleteNotice
  },
  setup(_, { emit }) {
    const injector = useReflectiveInjector([UserInfoDetailService, AccountManageLoginService])
    const toast = injector.get(ToastService)
    const user = injector.get(UserService)
    const loginService = injector.get(AccountManageLoginService)
    const cookie = injector.get(CookieService)

    const viewModel = reactive({
      openId: user.openId,
      avatar: user.avatar,
      loginType: user.loignType,
      nickname: user.nickname,
      isShowWechatVerification: false, // 微信验证
      isShowPhoneVerification: false, // 手机验证
      isShowWechatLogin: false, // 微信登录
      isShowVerificationFail: false, // 验证失败弹窗
      isShowAccountDeleteNotice: false, // 账号注销确认弹窗
      closeToken: '', // 注销令牌
    })

    // 当前登录类型的配置对象（用于渲染）
    const loginTypeObj = computed(() => {
      return LOGIN_TYPE_CONFIG[viewModel.loginType]
    })

    // 登录方式显示名称：手机号登录优先显示脱敏手机号，其他登录方式显示昵称
    const loginDisplayName = computed(() => {
      if (viewModel.loginType === LOGIN_TYPE_ID.PHONE && user.maskedPhone) {
        return user.maskedPhone
      }
      return viewModel.nickname
    })

    /**
     * @description 复制操作
     */
    function copyOpenId() {
      const aux = document.createElement('input')
      aux.value = viewModel.openId
      document.body.appendChild(aux)

      aux.select()

      document.execCommand('copy')
      document.body.removeChild(aux)
      toast.showToast({ message: '复制成功' })
    }

    /**
     * @description 关闭窗口
     */
    function closeWindow() {
      emit('cancel')
    }

    /**
     * 点击注销
     */
    function clickDeRegister() {
      // 根据登录类型展示对应的验证弹窗
      if (viewModel.loginType === LOGIN_TYPE_ID.WECHAT) {
        viewModel.isShowWechatVerification = true
      } else if (viewModel.loginType === LOGIN_TYPE_ID.PHONE) {
        viewModel.isShowPhoneVerification = true
      }
    }
 
    /**
     * 统一关闭弹窗方法
     * @param type - 弹窗类型
     */
    function closeModal(type: ModalType) {
      switch (type) {
        case 'wechat':
          viewModel.isShowWechatVerification = false
          break
        case 'phone':
          viewModel.isShowPhoneVerification = false
          break
        case 'wechatLogin':
          viewModel.isShowWechatLogin = false
          break
        case 'verificationFail':
          viewModel.isShowVerificationFail = false
          break
        case 'deleteNotice':
          viewModel.isShowAccountDeleteNotice = false
          break
      }
    }

    /**
     * 打开微信登录弹窗（从验证弹窗触发）
     */
    function openWechatLogin() {
      closeModal('wechat')
      viewModel.isShowWechatLogin = true
    }

    /**
     * 微信验证成功
     */
    function onWechatVerificationSuccess(token: string) {
      // 验证成功后关闭登录弹窗，继续注销流程
      closeModal('wechatLogin')
      viewModel.closeToken = token
      viewModel.isShowAccountDeleteNotice = true
    }

    /**
     * 微信验证失败
     */
    function onWechatVerificationError() {
      closeModal('wechatLogin')
      this.isShowVerificationFail = true
    }

    /**
     * 手机验证确认（验证手机号和验证码）
     */
    function onPhoneVerificationConfirm(verificationCode: string, phoneNumber: string) {
      // 调用验证接口，获取注销令牌
      loginService.requestGetTokenPhone({
        code: verificationCode,
        phone_number: phoneNumber
      })
        .then((res) => {
          const { status, close_token } = res
          if (status === VERIFICATION_STATUS.SUCCESS) {
            viewModel.closeToken = close_token
            viewModel.isShowAccountDeleteNotice = true
          } else if (status === VERIFICATION_STATUS.FAILED) {
            viewModel.isShowVerificationFail = true
          }
          closeModal('phone')
        })
        .catch((err) => {
          const ret = err.responseCommon?.ret

          // 需要显示验证失败弹窗
          if (ret === 4452009) {
            viewModel.isShowVerificationFail = true
            closeModal('phone')
            return
          }

          // 错误码提示映射
          const errorMap = {
            4572003: '验证码错误或已过期',
            4572004: '验证码无效',
          }
          toast.showToast({ message: errorMap[ret] || err.msg || '验证失败，请重试' })
        })
    }

    /**
     * 账号注销成功
     */
    function onDeleteAccountSuccess() {
      closeModal('deleteNotice')
      // 从快速登录列表中移除当前账号
      cookie.removeRecentLoginByOpenId(viewModel.openId)
      // 执行退出登录
      user.onLogout.next()
    }

    return {
      viewModel,
      loginTypeObj,
      loginDisplayName,
      closeWindow,
      copyOpenId,
      clickDeRegister,
      closeModal,
      openWechatLogin,
      onWechatVerificationSuccess,
      onWechatVerificationError,
      onPhoneVerificationConfirm,
      onDeleteAccountSuccess,
    }
  },
})
